import { isOptimisticTeamMessageId } from "./teamOptimisticMessage";

export const TEAM_MESSAGES_PAGE_SIZE = 50;
export const TEAM_MESSAGES_MAX_IN_MEMORY = 500;

function messageSortKey(message) {
  const createdAt = new Date(message?.createdAt).getTime();
  if (Number.isFinite(createdAt) && createdAt > 0) {
    return createdAt;
  }
  if (isOptimisticTeamMessageId(message?.id)) {
    const parts = String(message.id).split("-");
    const ts = Number(parts[1]);
    return Number.isFinite(ts) ? ts : 0;
  }
  return Number(message?.id) || 0;
}

export function sortMessagesById(list) {
  return [...list].sort((a, b) => {
    const keyDiff = messageSortKey(a) - messageSortKey(b);
    if (keyDiff !== 0) return keyDiff;
    const idA = isOptimisticTeamMessageId(a?.id) ? Number.MAX_SAFE_INTEGER : Number(a?.id) || 0;
    const idB = isOptimisticTeamMessageId(b?.id) ? Number.MAX_SAFE_INTEGER : Number(b?.id) || 0;
    return idA - idB;
  });
}

function shouldDropOptimisticDuplicate(optimistic, confirmed) {
  if (!isOptimisticTeamMessageId(optimistic?.id)) return false;
  if (optimistic?.sendStatus !== "sending") return false;
  if (String(optimistic.authorId) !== String(confirmed.authorId)) return false;
  if (optimistic.messageType !== confirmed.messageType) return false;

  const optimisticTime = new Date(optimistic.createdAt).getTime();
  const confirmedTime = new Date(confirmed.createdAt).getTime();
  if (!Number.isFinite(optimisticTime) || !Number.isFinite(confirmedTime)) return false;
  if (Math.abs(confirmedTime - optimisticTime) > 15000) return false;

  if (optimistic.messageType === "text") {
    return (
      String(optimistic.content || "").trim() === String(confirmed.content || "").trim()
    );
  }

  return true;
}

function removeOptimisticDuplicates(list) {
  const confirmed = list.filter((m) => !isOptimisticTeamMessageId(m?.id));
  if (!confirmed.length) return list;

  return list.filter((m) => {
    if (!isOptimisticTeamMessageId(m?.id) || m?.sendStatus !== "sending") return true;
    return !confirmed.some((c) => shouldDropOptimisticDuplicate(m, c));
  });
}

function trimMessages(list, mode) {
  if (list.length <= TEAM_MESSAGES_MAX_IN_MEMORY) return list;
  if (mode === "prepend") {
    return list.slice(0, TEAM_MESSAGES_MAX_IN_MEMORY);
  }
  return list.slice(-TEAM_MESSAGES_MAX_IN_MEMORY);
}

/**
 * @param {"replace"|"prepend"|"merge"} mode
 *   replace — carga inicial
 *   prepend — mensagens mais antigas (scroll para cima)
 *   merge   — novas mensagens ou refresh parcial (reações/recibos)
 */
export function mergeMessages(prev, incoming, mode = "replace") {
  const batch = incoming || [];

  if (mode === "replace") {
    return trimMessages(sortMessagesById(batch), "merge");
  }

  if (mode === "prepend") {
    const ids = new Set(prev.map((m) => m.id));
    const merged = sortMessagesById([...batch.filter((m) => !ids.has(m.id)), ...prev]);
    return trimMessages(merged, "prepend");
  }

  const map = new Map(prev.map((m) => [m.id, m]));
  for (const m of batch) {
    map.set(m.id, m);
  }
  return trimMessages(
    removeOptimisticDuplicates(sortMessagesById([...map.values()])),
    "merge"
  );
}

export function maxMessageId(messages) {
  if (!messages?.length) return null;
  let max = null;
  for (const message of messages) {
    if (isOptimisticTeamMessageId(message?.id)) continue;
    const id = Number(message?.id);
    if (!Number.isFinite(id)) continue;
    if (max == null || id > max) max = id;
  }
  return max;
}